import React, { useEffect, useMemo } from "react";
import { Button, Col, Container, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamation, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { SXDropdown } from "@common/components/ui-components/SXDropdown";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import type { UnitConverterType } from "@common/components/ui-components/SXEditField";
import type { NGDropdownListItem } from "@common/components/ui-components/SXDropdown";
import { useDispatch } from "react-redux";
import { useProject } from "../features/projects/projectSlice";
import { announceCrackingFollowsSecondOrder, checkSecondOrderVerticalPointLoad } from "../features/projects/secondOrderWarnings";
import type { CalculationMethod } from "../features/projects/projectSlice";
import { ng } from "@common/ngds";
import { InfoButton } from "./ui-components/InfoButton";
import bowImperfectionStructureImg from "../assets/images/bow-imperfection-structure.png";
import bowImperfectionNDiagramImg from "../assets/images/bow-imperfection-n-diagram.png";
import bowImperfectionVDiagramImg from "../assets/images/bow-imperfection-v-diagram.png";

// crkConv is stored as a plain ratio - the engine stops the crack loop once the largest relative
// change in an element's area or stiffness is at or below it - but is edited in per mille, which is
// the scale engineers think of it in. 1e-3 is what projGen seeds and what the engine's own tests use.
const DEFAULT_CRACK_CONVERGENCE = 1e-3;
// Above this the crack loop stops on its first pass, and the long-term strains the engine scales by
// (1 - 2 * crkConv) shrink towards zero - nothing up here is a tolerance anyone means.
const MAX_CRACK_CONVERGENCE = 0.1;
const PER_MILLE_UNIT: UnitConverterType = {
  toInput: (ratio: number) => ratio * 1000,
  fromInput: (perMille: number) => perMille / 1000,
  getDisplayString: () => "‰",
};

export const SXInputCalculation: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    project: selProject,
    setCalculationMethod,
    setConsiderCracking,
    setCrackConvergence,
    setSecondOrder,
    secondOrder,
    setInitialBowImperfection,
  } = useProject();

  const structure = selProject?.data?.data?.input?.structures?.[0];
  const rcElementDs = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0]?.designSettings?.rc;
  const calculationMethod: CalculationMethod =
    rcElementDs?.column?.secondOrderMethod === "SECOND_ORDER_METHOD_CURVATURE" ? "curvature" : "stiffness";

  // The Danish NA permits the nominal stiffness method only, so curvature is not offered there.
  const isDenmarkAnnex = selProject?.data?.data?.input?.ec?.nationalAnnex === "ANNEX_DENMARK";

  const rcGeneralDs = selProject?.data?.data?.input?.structures?.[0]?.designSettings?.rc;
  const considerCracking = rcGeneralDs?.crackCheck === true;
  const crackConvergence =
    typeof rcGeneralDs?.crkConv === "number" && Number.isFinite(rcGeneralDs.crkConv) && rcGeneralDs.crkConv > 0
      ? rcGeneralDs.crkConv
      : DEFAULT_CRACK_CONVERGENCE;

  const calculationMethodItems = useMemo<NGDropdownListItem<CalculationMethod>[]>(
    () => [
      { id: "stiffness", name: t("method_nominal_stiffness_.label") },
      { id: "curvature", name: t("method_nominal_curvature_.label") },
      { id: "general", name: t("general_method_.label") },
    ],
    [t],
  );

  const isDefaultCrackConvergence = Math.abs(crackConvergence - DEFAULT_CRACK_CONVERGENCE) < 1e-9;

  const visibleCalculationMethodItems = useMemo<NGDropdownListItem<CalculationMethod>[]>(
    () =>
      secondOrder
        ? calculationMethodItems.filter((item) => item.id === "general")
        : calculationMethodItems.filter(
          (item) => item.id !== "general" && !(isDenmarkAnnex && item.id === "curvature"),
        ),
    [secondOrder, calculationMethodItems, isDenmarkAnnex],
  );

  // A project can arrive at (or be switched to) the Danish annex while curvature is stored - put it
  // back on stiffness rather than leaving the dropdown on an option it no longer offers.
  useEffect(() => {
    if (isDenmarkAnnex && !secondOrder && calculationMethod === "curvature") {
      setCalculationMethod("stiffness");
    }
    // setCalculationMethod is re-created every render by useProject, so it is not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDenmarkAnnex, secondOrder, calculationMethod]);

  const effectiveCalculationMethod: CalculationMethod = secondOrder
    ? "general"
    : calculationMethod === "stiffness"
      ? "stiffness"
      : calculationMethod;

  const selectedMethodItem =
    visibleCalculationMethodItems.find((item) => item.id === effectiveCalculationMethod) ?? null;

  const isTimber = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0]?.beam?.timber !== undefined;
  const isSteel = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0]?.beam?.steel !== undefined;
  const isRC = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0]?.beam?.concrete !== undefined;

  let defaultBowImp = 400;
  if (isRC) {
    defaultBowImp = 400;
  } else if (isSteel) {
    defaultBowImp = 400;
  } else if (isTimber) {
    defaultBowImp = 500;

    const beam = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0]?.beam;

    const mat = selProject?.data?.data?.input?.mtrlDb.find(
      (mtrl) => mtrl.id?.guid && beam?.mtrlGuid && mtrl.id?.guid === beam?.mtrlGuid,
    );

    if (mat?.timberData?.product === "PRODUCT_TYPE_REGULAR") {
      defaultBowImp = 300;
    }
  }

  const bow_imp = useMemo(
    () => (selProject?.data.data?.input?.structures[0]?.elements[0].beam?.initialBowCoef as number) || defaultBowImp,
    [selProject],
  );

  return (
    <Container>
      <Row className="container-row">
        <Col xs={6} className="ps-0 px-0">
          <Form.Check
            type="switch"
            className="sx-switch"
            id="sx-second-order-ui-switch"
            label={t("2nd_order_analysis_.label")}
            checked={secondOrder}
            onChange={(e) => {
              if (e.target.checked) {
                setCalculationMethod("general");
              } else {
                setCalculationMethod("stiffness");
              }
              setSecondOrder(e.target.checked);
              // Switching 2nd order on is the moment the user asks for a second order result, so
              // it is the moment to say that a combination without a vertical point load will not
              // get one. Switching it off passes false, which retracts the warning again. The new
              // state is read from the event rather than from the store, which has not caught up
              // with setSecondOrder yet. See features/projects/secondOrderWarnings.
              checkSecondOrderVerticalPointLoad(structure, e.target.checked, dispatch);
              // Without cracking a 2nd order run of a concrete column has linear material, which is
              // not the general method of EN 1992-1-1 5.8.6 (#418). So for a concrete column
              // cracking follows 2nd order both ways - on with it (and locked, see the cracking
              // switch), off with it - and the user is told whenever that changes their input.
              const crackingAfter = isRC ? e.target.checked : considerCracking;
              if (crackingAfter !== considerCracking) {
                setConsiderCracking(crackingAfter);
                announceCrackingFollowsSecondOrder(crackingAfter, dispatch);
              }
            }}
          />
        </Col>
        {/* Cracking is a reinforced concrete check only, so the switch stays out of the way
            for other materials. */}
        {isRC && (
          <Col xs={6} className="ps-0 px-0">
            <Form.Check
              type="switch"
              className="sx-switch ps-4"
              id="sx-consider-cracking-switch"
              label={t("crack_consideration_.label")}
              checked={considerCracking}
              // Locked during 2nd order, which switched it on (see the 2nd order switch).
              disabled={secondOrder}
              onChange={(e) => setConsiderCracking(e.target.checked)}
            />
          </Col>
        )}
      </Row>
      {selProject?.data?.data?.input?.structures?.[0]?.elements?.[0]?.beam?.concrete !== undefined && (
        <>
          <Row className="container-row">
            <Col xs={6}>
              <Row>
                <Col xs={12} className="editfield-label">
                  {t("calculation_method_.label")}
                </Col>
                <Col xs={8} className="ps-0" style={{ height: 32 }}>
                  <SXDropdown
                    id="sx-calculation-method-dropdown"
                    toggleId="sx_calculation_method_toggle"
                    className="sx-dropdown"
                    dataTestId="dropdown_calculation_method"
                    name="calculation_method"
                    lang="en"
                    list={visibleCalculationMethodItems}
                    val={selectedMethodItem}
                    onUpdate={(value) => value && setCalculationMethod(value.id)}
                    disabled={secondOrder}
                    rawNames
                    localisation={false}
                  />
                </Col>
              </Row>
            </Col>
            {/* An advanced setting: the default suits almost every column, so the label carries a
                warning and the field says so whenever it has been moved off the default. */}
            <Col xs={6} className="ps-0 px-0">
              <Row>
                <Col xs={12} className="editfield-label d-flex align-items-center gap-2">
                  <span>{t("crack_convergence_.label")}</span>
                  <OverlayTrigger
                    trigger={["hover", "focus"]}
                    placement="bottom"
                    delay={{ show: 400, hide: 200 }}
                    overlay={
                      <Tooltip id="sx-crack-convergence-tooltip" className="sx-tooltip-wide">
                        {t("crack_convergence_info_.label")}
                      </Tooltip>
                    }
                  >
                    <span tabIndex={0} aria-label={t("crack_convergence_info_.label")}>
                      {/* The solid triangle's "!" is a cut-out that shows whatever is behind it, so a
                          black "!" is layered on top to keep it readable on a white panel. */}
                      <span className="fa-layers fa-fw">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="text-warning" />
                        <FontAwesomeIcon icon={faExclamation} transform="shrink-7 down-2" color="#000" />
                      </span>
                    </span>
                  </OverlayTrigger>
                </Col>
                <Col xs={8} className="ps-0 px-0 sx-per-mille-field">
                  <SXEditField
                    fullWidth={false}
                    textAlign="left"
                    id="sx-crack-convergence"
                    dataTestId="editfield_crack_convergence"
                    fontSize={12}
                    type="number"
                    disableNegativeNumber={true}
                    unit={PER_MILLE_UNIT}
                    val={crackConvergence}
                    disabled={!considerCracking}
                    onUpdate={(ratio: number) => {
                      // Out of range or cleared: dispatch nothing, and the field falls back to the
                      // stored value on blur.
                      if (Number.isFinite(ratio) && ratio > 0 && ratio <= MAX_CRACK_CONVERGENCE) {
                        setCrackConvergence(ratio);
                      }
                    }}
                  />
                </Col>
                {considerCracking && !isDefaultCrackConvergence && (
                  // Not .editfield-label: its colour is !important and would override the black.
                  <Col xs={12} className="ps-0 pt-1 d-flex align-items-center gap-2" style={{ color: "#000", fontSize: 12 }}>
                    <span>
                      {t("crack_convergence_not_default_.label", {
                        value: PER_MILLE_UNIT.toInput(DEFAULT_CRACK_CONVERGENCE),
                      })}
                    </span>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => setCrackConvergence(DEFAULT_CRACK_CONVERGENCE)}
                    >
                      {t("reset_.label")}
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </>
      )}

      {(selProject?.data?.data?.input?.structures?.[0]?.elements?.[0]?.beam?.concrete !== undefined || secondOrder) && (
        <>
          <Row className="container-row">
            <Col xs={12} className="editfield-label">
              {t("initial_bow_imperfection_.label")}, <var>x</var> (<var>M</var> = <var>N</var>
              <var>L</var> / <var>x</var>)
            </Col>
            <Col xs={11} className="ps-0 px-0">
              <SXEditField
                fullWidth={false}
                textAlign="left"
                id={"bowImperfection"}
                fontSize={12}
                type="number"
                disableNegativeNumber={true}
                val={bow_imp ? bow_imp : 400}
                onUpdate={(newValue: number) => {
                  setInitialBowImperfection(newValue);
                }}
              />
            </Col>
            <Col>
              <InfoButton
                id="info-initial-bow-imperfection"
                title={t("initial_bow_imperfection_.label")}
                size="xl"
                footer={(close) => (
                  <Button className="primary-btn" onClick={close}>
                    {t("close_.label", "Close")}
                  </Button>
                )}
              >
                <div id="info-popup" className="px-2 py-1 text-start">
                  <p className="mb-3">{t("bow_imperfection_info_intro_.label")}</p>
                  <ul className="mb-3 ps-4">
                    <li>{t("bow_imperfection_info_shape_.label")}</li>
                    <li>{t("bow_imperfection_info_components_.label")}</li>
                    <li>{t("bow_imperfection_info_variation_.label")}</li>
                    {/* Only true while the analysis adds the imperfection in a single direction - re-enable
                        this bullet if that stays the case, drop it once both directions are handled. */}
                    {/* <li>{t("bow_imperfection_info_direction_.label")}</li> */}
                  </ul>
                  <p className="mb-3">{t("bow_imperfection_info_figures_.label")}</p>
                  <div className="d-flex flex-wrap align-items-end justify-content-center gap-4">
                    <img
                      src={bowImperfectionStructureImg}
                      alt={t("bow_imperfection_info_alt_structure_.label")}
                      className="img-fluid"
                      style={{ maxHeight: 495, width: 321 }}
                    />
                    <img
                      src={bowImperfectionNDiagramImg}
                      alt={t("bow_imperfection_info_alt_n_diagram_.label")}
                      className="img-fluid"
                      style={{ maxHeight: 495, width: 184 }}
                    />
                    <img
                      src={bowImperfectionVDiagramImg}
                      alt={t("bow_imperfection_info_alt_v_diagram_.label")}
                      className="img-fluid"
                      style={{ maxHeight: 495, width: 184 }}
                    />
                  </div>
                </div>
              </InfoButton>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};
